import * as yup from 'yup';
import { controlFormSchema } from './schema.ts';

export type ControlDraftTableField =
  | 'clientControlId'
  | 'title'
  | 'controlWordingText'
  | 'otherFieldValidation';

export type ControlBulkDraftRow = {
  id: string;
  clientControlId?: string | null;
  title?: string | null;
  controlWordingText?: string | null;
  validation_errors?: Array<{ column: string; reason?: string; message?: string }>;
  /** Staged row from validate `file_data` (snake_case keys) for import. */
  stagedRow?: Record<string, unknown>;
};

export type ControlBulkField = ControlDraftTableField;

function errorText(e: { reason?: string; message?: string }): string {
  return String(e.reason ?? e.message ?? '').trim();
}

export function normalizeControlColumnLabel(column: string): string {
  return String(column || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

export function controlColumnMapsToField(column: string, field: ControlDraftTableField): boolean {
  const n = normalizeControlColumnLabel(column);
  switch (field) {
    case 'clientControlId':
      return (
        n === 'clientcontrolid' ||
        n === 'controlidclientid' ||
        n === 'clientreferralid' ||
        (n.includes('control') && n.includes('client') && n.includes('id'))
      );
    case 'title':
      return n === 'title' || n === 'controltitle' || (n.includes('control') && n.includes('title'));
    case 'controlWordingText':
      return (
        n === 'controlwordingtext' ||
        n === 'controldescription' ||
        (n.includes('control') && n.includes('description'))
      );
    case 'otherFieldValidation':
      return false;
    default:
      return false;
  }
}

export function reasonsForControlBackendField(
  errors: ControlBulkDraftRow['validation_errors'],
  field: ControlDraftTableField
): string[] {
  if (!errors?.length) return [];
  return errors
    .filter((e) => controlColumnMapsToField(String(e.column), field))
    .map((e) => errorText(e))
    .filter(Boolean);
}

export type ControlDraftRowMergeOptions = {
  showBackendErrors: boolean;
  applyFrontendValidation: boolean;
};

function looksLikeRequiredOrMissingReason(reason: string): boolean {
  const r = reason.toLowerCase();
  return (
    r.includes('required') ||
    r.includes('missing') ||
    r.includes('empty') ||
    r.includes('blank') ||
    r.includes('not provided')
  );
}

function isControlBackendCellErrorSupersededByCurrentValues(
  err: { column: string; reason?: string; message?: string },
  row: ControlBulkDraftRow,
  getFieldValue: (row: ControlBulkDraftRow, field: ControlDraftTableField) => unknown,
  _opts: ControlDraftRowMergeOptions
): boolean {
  const reason = errorText(err);
  if (!reason) return false;
  const col = String(err.column || '');

  if (!looksLikeRequiredOrMissingReason(reason)) return false;

  if (controlColumnMapsToField(col, 'clientControlId')) {
    return String(getFieldValue(row, 'clientControlId') ?? '').trim().length > 0;
  }
  if (controlColumnMapsToField(col, 'title')) {
    return String(getFieldValue(row, 'title') ?? '').trim().length > 0;
  }
  if (controlColumnMapsToField(col, 'controlWordingText')) {
    return String(getFieldValue(row, 'controlWordingText') ?? '').trim().length > 0;
  }
  return false;
}

export function effectiveControlBackendErrorsForRow(
  row: ControlBulkDraftRow,
  getFieldValue: (row: ControlBulkDraftRow, field: ControlDraftTableField) => unknown,
  opts: ControlDraftRowMergeOptions
): NonNullable<ControlBulkDraftRow['validation_errors']> {
  const raw = Array.isArray(row.validation_errors) ? row.validation_errors : [];
  if (!opts.showBackendErrors) return [];
  return raw.filter((e) => !isControlBackendCellErrorSupersededByCurrentValues(e, row, getFieldValue, opts));
}

export function orphanControlBackendReasons(errors: ControlBulkDraftRow['validation_errors']): string[] {
  if (!errors?.length) return [];
  const fields: ControlDraftTableField[] = [
    'clientControlId',
    'title',
    'controlWordingText',
    'otherFieldValidation',
  ];
  return errors
    .filter((e) => !fields.some((f) => controlColumnMapsToField(String(e.column), f)))
    .map((e) => {
      const r = errorText(e);
      const c = String(e.column || '').trim();
      if (c && r) return `${c}: ${r}`;
      return r || c;
    })
    .filter(Boolean);
}

export function stripControlBackendErrorsForMappedField(
  errors: ControlBulkDraftRow['validation_errors'] | undefined,
  field: ControlDraftTableField
): Array<{ column: string; reason?: string; message?: string }> {
  if (!errors?.length) return [];
  return errors.filter((e) => !controlColumnMapsToField(String(e.column), field));
}

const bulkRowShapeSchema = controlFormSchema.pick(['controlId', 'title']);

const bulkFormPathToTableField: Partial<Record<string, ControlDraftTableField>> = {
  controlId: 'clientControlId',
  title: 'title',
};

export type ControlFrontendFieldErrors = Partial<Record<ControlDraftTableField, string[]>>;

export function validateControlDraftRowFrontendSync(
  row: ControlBulkDraftRow,
  getFieldValue: (row: ControlBulkDraftRow, field: ControlDraftTableField) => unknown
): ControlFrontendFieldErrors {
  const shape = {
    controlId: String(getFieldValue(row, 'clientControlId') ?? '').trim(),
    title: String(getFieldValue(row, 'title') ?? '').trim(),
  };
  const out: ControlFrontendFieldErrors = {};
  try {
    bulkRowShapeSchema.validateSync(shape, { abortEarly: false });
  } catch (e: unknown) {
    if (e instanceof yup.ValidationError) {
      const inner = e.inner && e.inner.length > 0 ? e.inner : [e];
      for (const err of inner) {
        const path = err.path ? bulkFormPathToTableField[err.path] : undefined;
        if (!path) continue;
        if (out[path]?.length) continue;
        out[path] = [String(err.message || 'Invalid value')];
      }
    }
  }
  return out;
}

export function mergedControlFieldReasons(
  row: ControlBulkDraftRow,
  field: ControlDraftTableField,
  getFieldValue: (row: ControlBulkDraftRow, field: ControlDraftTableField) => unknown,
  options: ControlDraftRowMergeOptions
): string[] {
  const backendList = effectiveControlBackendErrorsForRow(row, getFieldValue, options);
  const backendField = options.showBackendErrors ? reasonsForControlBackendField(backendList, field) : [];

  if (field === 'otherFieldValidation') {
    return options.showBackendErrors ? orphanControlBackendReasons(backendList) : [];
  }

  if (field === 'controlWordingText') {
    return [];
  }

  const backendCoversField = backendField.length > 0;
  const frontend =
    options.applyFrontendValidation && !backendCoversField
      ? validateControlDraftRowFrontendSync(row, getFieldValue)[field] || []
      : [];

  return [...backendField, ...frontend];
}

const SELECTABLE_BLOCKING_FIELDS: ControlDraftTableField[] = ['clientControlId', 'title'];

export function controlDraftRowHasSelectableBlockingError(
  row: ControlBulkDraftRow,
  getFieldValue: (row: ControlBulkDraftRow, field: ControlDraftTableField) => unknown,
  options: ControlDraftRowMergeOptions
): boolean {
  return SELECTABLE_BLOCKING_FIELDS.some(
    (field) => mergedControlFieldReasons(row, field, getFieldValue, options).length > 0
  );
}

export function controlDraftRowHasAnyMergedError(
  row: ControlBulkDraftRow,
  getFieldValue: (row: ControlBulkDraftRow, field: ControlDraftTableField) => unknown,
  options: ControlDraftRowMergeOptions
): boolean {
  const fields: ControlDraftTableField[] = [
    'clientControlId',
    'title',
    'controlWordingText',
    'otherFieldValidation',
  ];
  return fields.some((f) => mergedControlFieldReasons(row, f, getFieldValue, options).length > 0);
}

export function selectedControlDraftRowsHaveMergedErrors(
  rows: ControlBulkDraftRow[],
  selectedIds: Set<string>,
  getFieldValue: (row: ControlBulkDraftRow, field: ControlDraftTableField) => unknown,
  options: ControlDraftRowMergeOptions
): boolean {
  for (const row of rows) {
    if (!selectedIds.has(row.id)) continue;
    if (controlDraftRowHasSelectableBlockingError(row, getFieldValue, options)) return true;
  }
  return false;
}

/** @deprecated Use mergedControlFieldReasons */
export function bulkFieldReasons(
  row: ControlBulkDraftRow,
  field: ControlDraftTableField,
  getFieldValue: (row: ControlBulkDraftRow, field: ControlDraftTableField) => unknown
): string[] {
  return mergedControlFieldReasons(row, field, getFieldValue, {
    showBackendErrors: true,
    applyFrontendValidation: true,
  });
}

/** @deprecated Use controlDraftRowHasAnyMergedError */
export function bulkRowHasError(
  row: ControlBulkDraftRow,
  getFieldValue: (row: ControlBulkDraftRow, field: ControlDraftTableField) => unknown
): boolean {
  return controlDraftRowHasAnyMergedError(row, getFieldValue, {
    showBackendErrors: true,
    applyFrontendValidation: true,
  });
}
